import type {
    TableColumnDefinition,
    TableRowId,
    DataGridProps} from "@fluentui/react-components";
import {
    DataGridBody,
    DataGridRow,
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridCell,
    TableCellLayout,
    createTableColumn,
    tokens
} from "@fluentui/react-components";
import type { IConfigurationInfo } from "../../interfaces";
import React from "react";
import { CheckmarkFilled, DismissFilled, SettingsCogMultipleRegular } from "@fluentui/react-icons";
import { getAppDisplayNameFromAppId } from "../../utilities/SettingsHelper/SettingsHelper";

export interface IConfigurationDataGridProps {
    settingsItems: IConfigurationInfo[];
    selectedItems: Set<TableRowId>;
    onSelectionChange(data: Set<TableRowId>): void;
}

const tableHeaders = {
    app: {
        id: "appId",
        title: "Application"
    },
    description: {
        id: "description",
        title: "Description"
    },
    enabled: {
        id: "enabled",
        title: "Deployed"
    },
    lastUpdated: {
        id: "lastUpdated",
        title: "Last Updated"
    }
};

const columns: TableColumnDefinition<IConfigurationInfo>[] = [
    createTableColumn<IConfigurationInfo>({
        columnId: tableHeaders.app.id,
        compare: (a, b) => {
            return a.appId.localeCompare(b.appId);
        },
        renderHeaderCell: () => {
            return tableHeaders.app.title;
        },
        renderCell: (item) => {
            return (
                <TableCellLayout>
                    {getAppDisplayNameFromAppId(item.appId)}
                </TableCellLayout>
            );
        },
    }),
    createTableColumn<IConfigurationInfo>({
        columnId: tableHeaders.description.id,
        compare: (a, b) => {
            return (a.description ?? "").localeCompare(b.description ?? "");
        },
        renderHeaderCell: () => {
            return tableHeaders.description.title;
        },
        renderCell: (item) => {
            return (
                <TableCellLayout truncate>
                    {item.description}
                </TableCellLayout>
            );
        },
    }),
    createTableColumn<IConfigurationInfo>({
        columnId: tableHeaders.enabled.id,
        compare: (a, b) => {
            return Number(a.isEnabled) - Number(b.isEnabled);
        },
        renderHeaderCell: () => {
            return tableHeaders.enabled.title;
        },
        renderCell: (item) => {
            return (
                <TableCellLayout>
                    {item.isEnabled ? <CheckmarkFilled /> : <DismissFilled />}
                </TableCellLayout>
            );
        },
    }),
    createTableColumn<IConfigurationInfo>({
        columnId: tableHeaders.lastUpdated.id,
        compare: (a, b) => {
            return (a.lastUpdated ?? "").localeCompare(b.lastUpdated ?? "");
        },
        renderHeaderCell: () => {
            return tableHeaders.lastUpdated.title;
        },
        renderCell: (item) => {
            const displayDate = new Date(`${item.lastUpdated}`).toLocaleString();
            return (
                <TableCellLayout>
                    {displayDate}
                </TableCellLayout>
            );
        },
    })
];

const GridHeader = () => {
    return (
        <DataGridHeader>
            <DataGridRow>
                {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
            </DataGridRow>
        </DataGridHeader>
    );
};

const GridBody = () => {
    return (
        <DataGridBody<IConfigurationInfo>>
            {({ item, rowId }) => (GridRow(rowId, item))}
        </DataGridBody>);
};

const GridRow = (id: React.Key, item: IConfigurationInfo) => {
    return (
        <DataGridRow<IConfigurationInfo> key={id} selectionCell={{ "aria-label": "Select row" }}>
            {({ renderCell }) => (<DataGridCell>{renderCell(item)}</DataGridCell>)}
        </DataGridRow>
    );
};

const columnSizingOptions = {
    appId: {
        minWidth: 150,
        defaultWidth: 180,
    },
    description: {
        defaultWidth: 1000,
        minWidth: 200,
    },
    enabled: {
        defaultWidth: 80,
        minWidth: 80,
        idealWidth: 80,
    },
    lastUpdated: {
        defaultWidth: 150,
        minWidth: 150,
        idealWidth: 150,
    },
};

const GetRowId = (item: IConfigurationInfo) => { return `${item.appId}:${item.id}`; };

export const ConfigurationDataGrid = (props: IConfigurationDataGridProps) => {
    const { settingsItems, selectedItems, onSelectionChange } = props;
    const noItems = settingsItems.length < 1;

    const defaultSortState = React.useMemo<Parameters<NonNullable<DataGridProps["onSortChange"]>>[1]>(() => ({
        sortColumn: tableHeaders.lastUpdated.id,
        sortDirection: "descending"
    }),
        []);

    return (
        <>
            <DataGrid
                items={settingsItems}
                columns={columns}
                selectionMode="single"
                selectedItems={selectedItems}
                sortable
                defaultSortState={defaultSortState}
                resizableColumns
                columnSizingOptions={columnSizingOptions}
                getRowId={GetRowId}
                onSelectionChange={(_e, data) => { onSelectionChange(data.selectedItems); }}>
                <GridHeader />
                <GridBody />
            </DataGrid>
            {noItems && <div style={{ marginTop: '30px', textAlign: 'center', verticalAlign: 'center' }} >
                <SettingsCogMultipleRegular primaryFill={tokens.colorNeutralForegroundDisabled} fontSize={250} />
                <h2>No configuration found for your tenant. Start by adding one.</h2>
            </div>}
        </>
    );
};

